import React, { useMemo, useState, useCallback, useContext } from "react";
import GraphUserComponent3 from "./GraphUserComponent3";
import mini_human_dark from "../../assets/media/images/twin-mini_dark.png";
import { darkColors } from "../../data/SampleData";
import { AppContext } from "../../AppContextProvider";
import NetworkStatsPanel from "./network/NetworkStatsPanel";
import NetworkInsightsPanel from "./network/NetworkInsightsPanel";
import NetworkLegend from "./network/NetworkLegend";
import DemoUserTooltip from "./network/DemoUserTooltip";
import HeroFemaleModel from "./network/HeroFemaleModel";
import { DEMO_STATISTICS, DEMO_INSIGHTS } from "../../data/demoNetworkData";

const DEMO_USERS = [
  { 
    id: "demo-user-1", 
    name: "User 1", 
    targetCid: 1,
    demographics: {
      gender: "Female",
      ageRange: "25-34",
      region: "Central Europe",
    },
    health: {
      bloodType: "A+",
      hasSurgeryHistory: true,
      surgeryTypes: ["Appendectomy"],
      chronicConditions: ["Mild asthma"],
      fitnessLevel: "Active",
    },
    lifestyle: {
      dietPreference: "Vegetarian",
      exerciseFrequency: "4-5 times/week",
      sleepQuality: "Good",
    },
    dataPoints: 847,
    matchScore: 78,
  },
  { 
    id: "demo-user-2", 
    name: "User 2", 
    targetCid: 2,
    demographics: {
      gender: "Male",
      ageRange: "35-44",
      region: "Western Europe",
    },
    health: {
      bloodType: "O-",
      hasSurgeryHistory: false,
      surgeryTypes: [],
      chronicConditions: [],
      fitnessLevel: "Moderate",
    },
    lifestyle: {
      dietPreference: "Omnivore",
      exerciseFrequency: "2-3 times/week",
      sleepQuality: "Average",
    },
    dataPoints: 523,
    matchScore: 65,
  },
  { 
    id: "demo-user-3", 
    name: "User 3", 
    targetCid: 3,
    demographics: {
      gender: "Female",
      ageRange: "18-24",
      region: "Northern Europe",
    },
    health: {
      bloodType: "B+",
      hasSurgeryHistory: false,
      surgeryTypes: [],
      chronicConditions: ["Seasonal allergies"],
      fitnessLevel: "Very Active",
    },
    lifestyle: {
      dietPreference: "Flexitarian",
      exerciseFrequency: "Daily",
      sleepQuality: "Excellent",
    },
    dataPoints: 689,
    matchScore: 82,
  },
  { 
    id: "demo-user-4", 
    name: "User 4", 
    targetCid: 4,
    demographics: {
      gender: "Male",
      ageRange: "45-54",
      region: "Southern Europe",
    },
    health: {
      bloodType: "AB+",
      hasSurgeryHistory: true,
      surgeryTypes: ["Knee replacement"],
      chronicConditions: ["Type 2 diabetes"],
      fitnessLevel: "Light",
    },
    lifestyle: {
      dietPreference: "Mediterranean",
      exerciseFrequency: "1-2 times/week",
      sleepQuality: "Average",
    },
    dataPoints: 412,
    matchScore: 54,
  },
  { 
    id: "demo-user-5", 
    name: "User 5", 
    targetCid: 5,
    demographics: {
      gender: "Non-binary",
      ageRange: "25-34",
      region: "Eastern Europe",
    },
    health: {
      bloodType: "A-",
      hasSurgeryHistory: true,
      surgeryTypes: ["Tonsillectomy", "Wisdom teeth"],
      chronicConditions: [],
      fitnessLevel: "Active",
    },
    lifestyle: {
      dietPreference: "Vegan",
      exerciseFrequency: "3-4 times/week",
      sleepQuality: "Good",
    },
    dataPoints: 756,
    matchScore: 71,
  },
  { 
    id: "demo-user-6", 
    name: "User 6", 
    targetCid: 6,
    demographics: {
      gender: "Female",
      ageRange: "55-64",
      region: "Central Europe",
    },
    health: {
      bloodType: "O+",
      hasSurgeryHistory: true,
      surgeryTypes: ["Cataract surgery"],
      chronicConditions: ["Hypertension"],
      fitnessLevel: "Moderate",
    },
    lifestyle: {
      dietPreference: "Low-sodium",
      exerciseFrequency: "Daily walks",
      sleepQuality: "Good",
    },
    dataPoints: 934,
    matchScore: 89,
  },
];

function getColorForCid(cid, level = 3, isEmpty = true) {
  const suffix = isEmpty ? "b" : "a";
  const groupName = `category${cid}${level}${suffix}`;
  const colorData = darkColors.find((k) => k.category === groupName);
  
  if (!colorData) {
    return {
      background: "#333355",
      border: "#555577",
      color: "#777FAC",
      bubbleSize: 48,
    };
  }
  
  return colorData;
}

function getGroupForCid(cid, level = 3, isEmpty = true) {
  const suffix = isEmpty ? "b" : "a";
  return `${cid}${level}${suffix}`;
}

export default function NetworkViewGraph({
  graphData,
  processedGraph,
  isUserGraph,
  setGraphData,
  isLocalRender,
  refreshGraph,
  graphRefreshed,
  isVisible,
}) {
  const context = useContext(AppContext);
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [focusedDemoUser, setFocusedDemoUser] = useState(null);

  const handleDemoUserHover = useCallback((nodeId, mousePosition) => {
    if (!nodeId) {
      setTooltipVisible(false);
      return;
    }
    
    const demoUser = DEMO_USERS.find(u => u.id === nodeId);
    if (demoUser) {
      setTooltipData(demoUser);
      setTooltipPosition(mousePosition);
      setTooltipVisible(true);
    }
  }, []);

  const handleDemoUserClick = useCallback((nodeId) => {
    if (!nodeId) return;
    
    const demoUser = DEMO_USERS.find(u => u.id === nodeId);
    if (demoUser) {
      setFocusedDemoUser(demoUser);
      setTooltipVisible(false);
    }
  }, []);

  const handleCentralUserClick = useCallback(() => {
    setFocusedDemoUser(null);
  }, []);

  const handleSidebarModelClick = useCallback(() => {
    console.log("[NetworkViewGraph] Sidebar model clicked", { 
      hasContext: !!context, 
      hasOpenModal: !!(context && context.openModal),
      hasGraphData: !!graphData,
      hasNodes: !!(graphData && graphData.nodes),
      nodesCount: graphData?.nodes?.length
    });
    if (context && context.openModal && graphData && graphData.nodes) {
      context.openModal("categoriesmodal", "", graphData.nodes);
    }
  }, [context, graphData]);

  const enhancedGraphData = useMemo(() => {
    if (!graphData || !graphData.nodes || !graphData.edges) {
      return graphData;
    }

    let baseNodes = graphData.nodes;
    let baseEdges = graphData.edges;
    
    if (focusedDemoUser) {
      const focusedCid = focusedDemoUser.targetCid;
      
      baseNodes = graphData.nodes.filter(node => {
        const isCentralUser = node.cid === 0 || node.group === "00a";
        const isFocusedCategory = node.cid === focusedCid;
        return isCentralUser || isFocusedCategory;
      });
      
      const filteredNodeIds = new Set(baseNodes.map(n => n.id));
      baseEdges = graphData.edges.filter(edge => 
        filteredNodeIds.has(edge.from) && filteredNodeIds.has(edge.to)
      );
    }

    const newNodes = baseNodes.map((node) => {
      if (node.group === "00a" || node.cid === 0) {
        return {
          ...node,
          shape: "image",
          image: mini_human_dark,
          size: 80,
          label: node.label || "You",
        };
      }
      return node;
    });
    
    const newEdges = [...baseEdges];

    const usersToAdd = focusedDemoUser 
      ? DEMO_USERS.filter(u => u.id === focusedDemoUser.id)
      : DEMO_USERS;

    usersToAdd.forEach((demoUser) => {
      const targetCategories = newNodes.filter(
        (n) => n.cid === demoUser.targetCid && !n.isFile
      );

      if (targetCategories.length === 0) return;

      const randomTarget =
        targetCategories[Math.floor(Math.random() * targetCategories.length)];

      const targetCid = demoUser.targetCid;
      const pathColor1 = getColorForCid(targetCid, 4, false);
      const pathColor2 = getColorForCid(targetCid, 5, false);

      const glowColor = pathColor1.background || "#e91e8c";
      
      newNodes.push({
        id: demoUser.id,
        label: demoUser.name,
        _type: "demo-user",
        cid: 9,
        group: "demoUser",
        shape: "image",
        image: mini_human_dark,
        size: 60,
        glowColor: glowColor,
        demoUserData: demoUser,
        font: {
          color: "#FFFFFF",
          size: 16,
          face: "Inter, sans-serif",
        },
        physics: true,
        fixed: false,
      });

      const pathId1 = `${demoUser.id}-path-1`;
      const pathId2 = `${demoUser.id}-path-2`;

      newNodes.push({
        id: pathId1,
        label: "",
        _type: "path-node",
        cid: targetCid,
        group: getGroupForCid(targetCid, 4, false),
        shape: "dot",
        size: 25,
        color: {
          background: pathColor1.background,
          border: pathColor1.border,
          highlight: {
            background: pathColor1.background,
            border: "#FFFFFF",
          },
        },
        borderWidth: 3,
        physics: true,
        fixed: false,
      });

      newNodes.push({
        id: pathId2,
        label: "",
        _type: "path-node",
        cid: targetCid,
        group: getGroupForCid(targetCid, 5, false),
        shape: "dot",
        size: 18,
        color: {
          background: pathColor2.background,
          border: pathColor2.border,
          highlight: {
            background: pathColor2.background,
            border: "#FFFFFF",
          },
        },
        borderWidth: 3,
        physics: true,
        fixed: false,
      });

      const edgeColor = "#777FAC";

      newEdges.push({
        from: demoUser.id,
        to: pathId1,
        color: { color: edgeColor, opacity: 1 },
        width: 1,
        smooth: { type: "curvedCW", roundness: 0.15 },
        physics: true,
      });

      newEdges.push({
        from: pathId1,
        to: pathId2,
        color: { color: edgeColor, opacity: 1 },
        width: 1,
        smooth: { type: "curvedCW", roundness: 0.15 },
        physics: true,
      });

      newEdges.push({
        from: pathId2,
        to: randomTarget.id,
        color: { color: edgeColor, opacity: 1 },
        width: 1,
        smooth: { type: "curvedCW", roundness: 0.15 },
        physics: true,
      });
    });

    return {
      ...graphData,
      nodes: newNodes,
      edges: newEdges,
    };
  }, [graphData, focusedDemoUser]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="network-view-container" style={{ width: "100%", height: "100%", position: "relative" }}>
      <GraphUserComponent3
        key={focusedDemoUser ? `focused-${focusedDemoUser.id}` : 'network-full'}
        graphData={enhancedGraphData}
        processedGraph={processedGraph}
        isUserGraph={isUserGraph}
        setGraphData={setGraphData}
        isLocalRender={isLocalRender}
        refreshGraph={refreshGraph}
        graphRefreshed={graphRefreshed}
        isNetworkViewMode={true}
        isGrapView={true}
        onDemoUserHover={handleDemoUserHover}
        onDemoUserClick={handleDemoUserClick}
        onCentralUserClick={handleCentralUserClick}
        onSidebarModelClick={handleSidebarModelClick}
        focusedDemoUser={focusedDemoUser}
      />
      
      
      <DemoUserTooltip
        userData={tooltipData}
        position={tooltipPosition}
        visible={tooltipVisible}
      />
      
      <div className="network-overlay-panels">
        <div className="network-left-panel">
          <NetworkStatsPanel statistics={DEMO_STATISTICS} />
          <NetworkLegend />
        </div>
        
        <div className="network-bottom-panel">
          <NetworkInsightsPanel insights={DEMO_INSIGHTS} />
        </div>
      </div>
    </div>
  );
}
